import type { Agent, Channel, EventDetails, Message, RoomReference, RunEvent } from '../api/types'

/**
 * What to call a room on screen.
 *
 * The repository key is the identity and stays the identity — but it is
 * `github.com_owner_repo`, and three of those in a 200px rail truncate to three
 * identical `github.com_mb…`. The design names rooms `stagepost`, and that is
 * the readable half of the key.
 *
 * A linked repo gives the short name exactly, no guessing. Otherwise the key is
 * split on `_`, which is right for a remote-derived key and left alone for
 * anything that does not look like one — a directory-basename fallback key has
 * no host segment to drop.
 */
export function roomName(channel: Pick<Channel, 'repository_key' | 'github_full_name'>): string {
  if (channel.github_full_name?.includes('/')) {
    return channel.github_full_name.split('/').pop() ?? channel.repository_key
  }
  const segments = channel.repository_key.split('_')
  // `github.com_owner_repo`: a host segment, an owner, and the rest.
  if (segments.length >= 3 && segments[0].includes('.')) return segments.slice(2).join('_')
  return channel.repository_key
}

/** The short name of the room an agent is visiting from. */
export function visitingName(from: RoomReference): string {
  return roomName({ repository_key: from.repository_key, github_full_name: null })
}

/** What to call an agent on screen: the nickname it gave itself, or its handle. Tags stay the handle. */
export function displayName(handle: string, nickname: string | null | undefined): string {
  return nickname ?? handle
}

/** Day boundaries, because in a three-day room time is the main axis. */
export function dayLabel(when: string, now = new Date()): string {
  const posted = new Date(when)
  const days = Math.floor(
    (startOfDay(now).getTime() - startOfDay(posted).getTime()) / 86_400_000,
  )
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return posted.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
}

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate())
}

export function clockOf(when: string): string {
  const posted = new Date(when)
  return `${String(posted.getHours()).padStart(2, '0')}:${String(
    posted.getMinutes(),
  ).padStart(2, '0')}`
}

export interface StreamRow {
  key: string
  /** `replies` divides a thread's first message from its replies. */
  kind: 'day' | 'new' | 'message' | 'replies'
  label?: string
  message?: Message
  /** Drawn under the message above it, without a face or a name. */
  continued?: boolean
}

/** How long one sender's messages keep running under one face, as in Slack. */
const GROUP_WITHIN = 5 * 60_000

/** A run's own `running as`, `moved to`, `did not start` and `ended`: they update the line under its start, never a row. */
export function isRunFollowUp(message: Message): boolean {
  return Boolean(message.event?.run && message.reply_to)
}

/** One event of a run: what it says, and the run's state when it was posted. */
export type RunStep = Pick<EventDetails, 'title' | 'meta'> & { run: RunEvent }

const RUN_STATE_WORDS: Record<string, string> = {
  requested: 'starting', starting: 'starting', running: 'working', ended: 'ended',
}

/** The newest event of a run the room holds, which is the start itself until a follow-up lands. */
export function latestRunStep(messages: Message[], start: RunStep): RunStep {
  let newest = start
  for (const message of messages) {
    const run = message.event?.run
    if (message.event && run?.id === start.run.id) newest = { ...message.event, run }
  }
  return newest
}

/**
 * The line under a run's start: "started <branch> on <machine> · <state>",
 * leaving out what is not known yet, read from the run's newest event. A run
 * started from an agent's proposal says whose, which only its start records.
 */
export function runLine(messages: Message[], start: RunStep): string {
  const { run, meta } = latestRunStep(messages, start)
  const proposedBy = start.run.proposed_by
  const state = run.state === 'handed_over'
    ? (run.harness_state ? `in ${run.harness_state}` : 'handed over')
    : run.state === 'failed'
      ? (meta ? `did not start: ${meta}` : 'did not start')
      : RUN_STATE_WORDS[run.state] ?? run.state
  const where = ['started', run.branch, run.machine && `on ${run.machine}`].filter(Boolean).join(' ')
  return [where, proposedBy && `as ${proposedBy} proposed`, state].filter(Boolean).join(' · ')
}

/**
 * The label after an agent's name for where it runs: the room a run started
 * it from, else the surface it reports, else nothing. `fromRoom` is the one
 * the room did itself.
 */
export function whereAgentRuns(
  agent: Agent | undefined, channelId: string,
): { text: string; fromRoom: boolean } | null {
  const run = agent?.started_by_run
  if (run) {
    return {
      text: run.channel_id === channelId
        ? 'FROM THIS ROOM'
        : `FROM #${roomName({ repository_key: run.repository_key, github_full_name: null })}`,
      fromRoom: true,
    }
  }
  return agent?.surface ? { text: agent.surface.toUpperCase(), fromRoom: false } : null
}

/**
 * The oldest message of the thread `message` is in that `byId` holds: the
 * thread's first message, `message` itself when it is one, unless the room's
 * page, which holds only the newest messages, starts after it.
 */
function oldestIn(byId: Map<string, Message>, message: Message): Message {
  let oldest = message
  while (oldest.reply_to) {
    const parent = byId.get(oldest.reply_to)
    if (!parent) break
    oldest = parent
  }
  return oldest
}

const byIdOf = (messages: Message[]) => new Map(messages.map((message) => [message.id, message]))

/** Where a thread opens: on the oldest message of it the page holds, which a reply then answers. */
export function oldestHeldOf(messages: Message[], message: Message): Message {
  return oldestIn(byIdOf(messages), message)
}

/** The first message of the thread `message` is in, or null when the page starts after it. */
export function threadRootOf(messages: Message[], message: Message): Message | null {
  const oldest = oldestHeldOf(messages, message)
  return oldest.reply_to ? null : oldest
}

/**
 * What the room's stream shows: first messages, replies also sent to the
 * room, and replies whose thread starts before the page does, which show with
 * the line saying what they answer.
 */
export function roomMessagesOf(messages: Message[]): Message[] {
  const byId = byIdOf(messages)
  return messages.filter((message) => !message.reply_to || message.shown_in_room || oldestIn(byId, message).reply_to)
}

/**
 * Each thread's replies in the order they were posted, by the id of the
 * oldest message of it the page holds. A reply to a reply is in the same
 * thread. A run's own lines are events, folded into the run line, and no
 * reply, as `domain.thread_participants` has it.
 */
export function threadsOf(messages: Message[]): Map<string, Message[]> {
  const byId = byIdOf(messages)
  const threads = new Map<string, Message[]>()
  for (const message of messages) {
    if (!message.reply_to || message.sender_type === 'event') continue
    const rootId = oldestIn(byId, message).id
    // The oldest held reply of a thread whose start is not on the page stands in for its first message.
    if (rootId === message.id) continue
    const replies = threads.get(rootId)
    if (replies) replies.push(message)
    else threads.set(rootId, [message])
  }
  return threads
}

export function countOfReplies(count: number): string {
  return `${count} ${count === 1 ? 'reply' : 'replies'}`
}

/**
 * Who a person's reply in a thread reaches, counted as the server counts it
 * (`domain.thread_participants`): the first message's sender unless that is
 * an event other than a run's start, each replier, then the agent the run
 * became. Lowercased, once each, and never the reader.
 */
export function threadReach(messages: Message[], root: Message, replies: Message[], reader: string): string[] {
  const run = root.event?.run
  const handles = replies.map((reply) => reply.sender)
  if (root.sender_type !== 'event' || run) handles.unshift(root.sender)
  const agent = root.event && run ? latestRunStep(messages, { ...root.event, run }).run.branch : null
  if (agent) handles.push(agent)
  return [...new Set(handles.map((handle) => handle.toLowerCase()))]
    .filter((handle) => handle !== reader.toLowerCase())
}

/**
 * Whether a message tags the reader: `@` and their display name lowercased,
 * the handle the server's activity reads, or an agent's `@here`. A person's
 * `@here` asks the agents, not the people reading along.
 */
export function tagsReader(message: Message, readerName: string): boolean {
  return message.mentions.includes(readerName.toLowerCase())
    || (message.sender_type === 'agent' && message.mentions.includes('here'))
}

/**
 * Whether a person has replied to a question, which answers it whatever they
 * said, and the option they chose: from `answered_with`, or else from the first
 * reply from a person in `messages`, so a live answer counts at once. As in
 * `domain.chosen_option`, a leading `@<agent>` is taken off. */
export function answerOf(question: Message, messages: Message[]): { replied: boolean; chosen: string | null } {
  if (question.answered_with) return { replied: true, chosen: question.answered_with }
  const reply = messages.find((message) => message.reply_to === question.id && message.sender_type === 'human')
  if (!reply) return { replied: false, chosen: null }
  const tag = `@${question.sender}`.toLowerCase()
  let text = reply.body.trim()
  if (text.toLowerCase().startsWith(tag) && /^(\s|$)/.test(text.slice(tag.length))) text = text.slice(tag.length).trim()
  return { replied: true, chosen: question.options?.includes(text) ? text : null }
}

/**
 * Interleave day dividers and the New line into the message list.
 *
 * The New line goes before `newLineBefore` and stays there while you read:
 * what arrives stacks below it, and what you read keeps its place above or
 * below it, so "what did I miss" is answerable without scrolling back. Ids
 * are `<ms-epoch>-<rand>` and sort as strings, so a first unread message
 * older than the page puts the line above the first message.
 */
export function buildStream(
  messages: Message[],
  newLineBefore: string | null,
  now = new Date(),
): StreamRow[] {
  const rows: StreamRow[] = []
  let currentDay: string | null = null
  let markerPlaced = false

  // The message drawn last, or null once a divider has come between.
  let above: Message | null = null

  for (const message of messages) {
    if (isRunFollowUp(message)) continue
    const day = dayLabel(message.posted_at, now)
    if (day !== currentDay) {
      currentDay = day
      above = null
      rows.push({ key: `day-${day}-${message.id}`, kind: 'day', label: day })
    }

    if (!markerPlaced && newLineBefore && message.id >= newLineBefore) {
      markerPlaced = true
      above = null
      rows.push({ key: 'new-line', kind: 'new' })
    }

    // A reply in the room opens with the thread it answers above a name, and
    // in a conversation with its thread label, so it never joins the group above.
    const continued = above !== null && message.sender_type !== 'event' && !message.reply_to
      && above.sender === message.sender && above.sender_type === message.sender_type
      && Date.parse(message.posted_at) - Date.parse(above.posted_at) <= GROUP_WITHIN
    rows.push({ key: message.id, kind: 'message', message, continued })
    above = message
  }
  return rows
}

/** A thread: its first message, how many replied, then the replies, with no day dividers. */
export function buildThread(root: Message, replies: Message[]): StreamRow[] {
  const rows: StreamRow[] = [{ key: root.id, kind: 'message', message: root }]
  if (replies.length) rows.push({ key: 'replies', kind: 'replies', label: countOfReplies(replies.length) })
  return [...rows, ...buildStream(replies, null).filter((row) => row.kind !== 'day')]
}

/**
 * The line at the top of the stream, where history stops.
 *
 * A three-day room has a floor, and an unexplained one reads as a bug — people
 * scroll up looking for the rest. Saying it plainly, in the serif the humans
 * speak in, turns a missing thing into a stated policy.
 */
export function horizonLine(retentionDays: number, now = new Date()): string {
  const floor = new Date(now)
  floor.setDate(floor.getDate() - retentionDays)
  const when = floor.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
  const kept = retentionDays === 1 ? 'a day' : `${retentionDays} days`
  return `Everything before ${when} has been deleted. The room keeps ${kept}.`
}

/**
 * Whether anybody has worked in this room: an agent on the roster, or anything
 * said. The roster alone cannot answer it, because it lists only agents seen
 * inside the retention horizon, so a room whose agents all stopped days ago has
 * an empty roster and a stream full of what they said. The roster pane and the
 * composer both ask this, so they cannot disagree about which room is new.
 */
export function hasHistory(roster: Agent[], messages: Message[]): boolean {
  return roster.length > 0 || messages.length > 0
}

/** When the phone's bar says the unread began: "14:39", "yesterday 14:39", "8 October 14:39". */
export function sinceLabel(when: string, now = new Date()): string {
  const day = dayLabel(when, now)
  if (day === 'Today') return clockOf(when)
  return `${day === 'Yesterday' ? 'yesterday' : day} ${clockOf(when)}`
}

/** Long bodies clamp; agents write forty-line findings and that is the point. */
export const CLAMP_AFTER_CHARACTERS = 300

export function isLong(message: Message): boolean {
  return (message.body ?? '').length > CLAMP_AFTER_CHARACTERS
}

/** Split a body into plain text, `code` and @mentions, for rendering. */
export interface BodyPart {
  text: string
  kind: 'text' | 'code' | 'mention'
  /**
   * For a mention, the handle without its `@`, lowercased. The renderer needs
   * it to say whether an agent, a person, or nobody at all was named — three
   * things that used to look identical.
   */
  handle?: string
}

export function parseBody(body: string): BodyPart[] {
  const parts: BodyPart[] = []
  // The slash group carries branch-shaped names: every agent is called
  // something like `claude/policy-partner-coverage-5d84df`, and stopping at
  // the slash rendered half a name and matched nobody.
  const pattern = /((?<![\w.])@[a-z][a-z0-9_-]*(?:\/[a-z0-9_-]+)*)|(`[^`]+`)/gi
  let last = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(body)) !== null) {
    if (match.index > last) {
      parts.push({ text: body.slice(last, match.index), kind: 'text' })
    }
    if (match[1]) {
      parts.push({ text: match[1], kind: 'mention',
                   handle: match[1].slice(1).toLowerCase() })
    }
    else parts.push({ text: match[2].slice(1, -1), kind: 'code' })
    last = match.index + match[0].length
  }
  if (last < body.length) parts.push({ text: body.slice(last), kind: 'text' })
  return parts.length ? parts : [{ text: body, kind: 'text' }]
}

/**
 * The delivery line under a posted human message.
 *
 * Deliberately descriptive, not a promise. Saying "atlas will receive this"
 * would be fiction until the engine schedules delivery; saying who is being
 * asked and who is only watching is simply true.
 *
 * A person typing here has no reason to type except to reach the agents, so an
 * untagged message asks all of them. Naming one narrows it to that one, which
 * is how a question stops waking the whole room.
 */
export function deliveryLine(mentions: string[], roomAgents: string[]): string {
  if (!mentions.length) return 'nobody named · every agent is asked to answer'
  if (mentions.includes('here')) return '@here · every agent is asked to answer'
  // The room's own agents read the room; a visitor reads only what names it.
  const others = roomAgents.filter((name) => !mentions.includes(name.toLowerCase())).length
  return `${mentions.join(', ')} asked · the other ${others} see it as context`
}

/**
 * A message is written for two readers at once: an opening a person can take in
 * at a glance, then the detail another agent needs. The room always shows the
 * opening and keeps the rest behind "Show more", so an agent can be exhaustive
 * without burying the people reading over its shoulder.
 *
 * The split is the first blank line, which is what writing two paragraphs
 * already produces — no markers to remember, and nothing to strip when an agent
 * forgets. A body without one has no detail half and falls back to the length
 * clamp, which is also what every message written before this convention does.
 */
export interface SplitBody {
  summary: string
  detail: string
}

export function splitBody(body: string): SplitBody {
  const text = body ?? ''
  const separator = text.match(/\n[ \t]*\n/)
  if (!separator || separator.index === undefined) return { summary: text, detail: '' }
  return {
    summary: text.slice(0, separator.index).trim(),
    detail: text.slice(separator.index + separator[0].length).trim(),
  }
}
